import { Check } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { CurrencyInput } from '@/components/molecules/CurrencyInput';
import { DatePicker } from '@/components/molecules/date-picker';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';
import type { CustomFieldSchema } from '@/types/custom-fields';
import {
  dateStringToDate,
  dateToDateString,
  getCustomFieldOptions,
  parseMultiSelectDraftValue,
  serializeMultiSelectDraftValue,
} from './custom-field-form-utils';

interface CustomFieldValueInputProps {
  schema: CustomFieldSchema;
  id: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  className?: string;
  /**
   * When true, signals an inline validation error on the inner control —
   * forwarded to the underlying input as `aria-invalid` and used to paint a
   * destructive ring so the user can spot which field needs fixing.
   */
  ariaInvalid?: boolean;
  /** Optional `aria-describedby` reference, typically the id of an error message. */
  ariaDescribedBy?: string;
}

function toCurrencyDisplayValue(value: string): number | '' {
  if (value.trim() === '') return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
}

export function CustomFieldValueInput({
  schema,
  id,
  value,
  onChange,
  required,
  className,
  ariaInvalid,
  ariaDescribedBy,
}: CustomFieldValueInputProps) {
  const options = getCustomFieldOptions(schema) ?? [];
  const fieldType = schema.definition.fieldType;

  const invalidClass = ariaInvalid
    ? 'ring-1 ring-destructive border-destructive focus-visible:ring-destructive'
    : undefined;

  if (fieldType === 'TEXT') {
    // Clip at the configured maxLength so the user can't type past it and hit a
    // server rejection on save (mirrors MULTI_SELECT's client-side cap).
    const textConfig = schema.definition.config as { maxLength?: number } | undefined;
    const maxLength =
      typeof textConfig?.maxLength === 'number' && textConfig.maxLength > 0
        ? textConfig.maxLength
        : undefined;
    return (
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        maxLength={maxLength}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn('h-8 text-sm', invalidClass, className)}
      />
    );
  }

  if (fieldType === 'NUMBER') {
    return (
      <Input
        id={id}
        type="number"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn('h-8 text-sm', invalidClass, className)}
      />
    );
  }

  if (fieldType === 'CURRENCY') {
    return (
      <CurrencyInput
        id={id}
        value={toCurrencyDisplayValue(value)}
        onChange={(next) => onChange(next === '' ? '' : String(next))}
        required={required}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn('h-8 text-sm', invalidClass, className)}
      />
    );
  }

  if (fieldType === 'DATE') {
    return (
      <DatePicker
        value={dateStringToDate(value)}
        onChange={(date) => onChange(dateToDateString(date))}
        placeholder="Pick a date"
        className={cn('h-8 text-sm', invalidClass, className)}
      />
    );
  }

  if (fieldType === 'CHECKBOX') {
    return (
      <div className={cn('flex items-center gap-2 py-1', className)}>
        <Checkbox
          id={id}
          checked={value === 'true'}
          onCheckedChange={(checked) => onChange(checked ? 'true' : 'false')}
        />
        <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
          {schema.definition.label}
        </Label>
      </div>
    );
  }

  if (fieldType === 'MULTI_SELECT') {
    const selectedKeys = parseMultiSelectDraftValue(value);
    const selectedOptions = options.filter((option) => selectedKeys.includes(option.key));
    // NOT-174 / NOT-178: enforce maxSelections on the client so the user can
    // never submit an over-limit payload. We still rely on the BE to reject
    // bogus payloads, but blocking here means the success toast can't fire on
    // a write the BE would have rejected.
    const config = schema.definition.config as { maxSelections?: number } | undefined;
    const maxSelections =
      typeof config?.maxSelections === 'number' && config.maxSelections > 0
        ? config.maxSelections
        : null;
    const atLimit = maxSelections !== null && selectedKeys.length >= maxSelections;

    function toggleOption(key: string) {
      const isCurrentlySelected = selectedKeys.includes(key);
      // Block adding past the configured Max Selections cap. Removing is always
      // allowed regardless of cap so users can fix an over-limit legacy value.
      if (!isCurrentlySelected && atLimit) {
        return;
      }
      const next = isCurrentlySelected
        ? selectedKeys.filter((item) => item !== key)
        : [...selectedKeys, key];
      onChange(serializeMultiSelectDraftValue(next));
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-invalid={ariaInvalid || undefined}
            aria-describedby={ariaDescribedBy}
            className={cn(
              'min-h-8 h-auto w-full justify-start px-2 py-1 text-left text-sm font-normal',
              invalidClass,
              className,
            )}
          >
            {selectedOptions.length > 0 ? (
              <span className="flex flex-wrap gap-1">
                {selectedOptions.map((option) => (
                  <Badge key={option.key} variant="secondary">
                    {option.label}
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="text-muted-foreground">Select...</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-1">
          {maxSelections !== null ? (
            <p
              data-testid="custom-field-multi-select-limit"
              className={cn(
                'px-2 pb-1 pt-1 text-[11px] text-muted-foreground',
                atLimit && 'text-amber-600 dark:text-amber-400',
              )}
              aria-live="polite"
            >
              {selectedKeys.length} / {maxSelections} selected
              {atLimit ? ' (limit reached)' : ''}
            </p>
          ) : null}
          {options.length > 0 ? (
            options.map((option) => {
              const selected = selectedKeys.includes(option.key);
              const disabled = !selected && atLimit;
              return (
                <button
                  key={option.key}
                  type="button"
                  disabled={disabled}
                  aria-disabled={disabled || undefined}
                  title={
                    disabled
                      ? `Max ${maxSelections} selection${maxSelections === 1 ? '' : 's'} reached`
                      : undefined
                  }
                  className={cn(
                    'flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm hover:bg-muted',
                    disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                  )}
                  onClick={() => toggleOption(option.key)}
                >
                  <span>{option.label}</span>
                  {selected ? <Check className="size-4 text-primary" /> : null}
                </button>
              );
            })
          ) : (
            <p className="px-3 py-4 text-sm text-muted-foreground">No options configured.</p>
          )}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        id={id}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        className={cn('h-8 text-sm', invalidClass, className)}
      >
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.key} value={option.key}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
